/**
 * THE FULL-CATALOG TOOLBAR — every capability on screen at once, for astro + admin.
 *
 * <h2>⚠ THE REVIEW IT WAS BUILT FOR HAS CONCLUDED, AND IT IS MUCH NARROWER NOW</h2>
 *
 * It began as a complete, unconstrained inventory so the tools could be reviewed as a set and a
 * subset chosen for the Regular and Trial roles. That review happened: the engine runners, the
 * geometry tools, the utility cluster and the two batch scans were all assigned to Admin Panel
 * tabs on 2026-11-01. So the one thing this file bypassed that mattered — RELOCATION — is exactly
 * the thing it must now honour, because the panel is where those tools were decided to live.
 *
 * <h2>⚠ WHAT IT BYPASSES, AND WHAT IT DELIBERATELY DOES NOT</h2>
 *
 * <ul>
 *   <li><b>{@code PRODUCT_DENIES}</b> — the three astro entries;</li>
 *   <li><b>the short-bar preset</b> — so the catalog is complete whichever bar the reader prefers,
 *       without this changing their stored preference.</li>
 * </ul>
 *
 * <p>⚠ AND IT NO LONGER BYPASSES RELOCATION OR SOFT DELETION. A relocated tool is not parked — it
 * has a home, with a tab and a group, and surfacing it on the toolbar as well would be two answers
 * to "where does this live". That was defensible while the inventory was being read and is not
 * once the answers are recorded.
 *
 * <p>Soft deletion follows for a simpler reason: the list is EMPTY. Auto-Analysis and Run Global
 * Scan graduated to relocation, so there is nothing left for that bypass to surface and keeping it
 * would be a branch with no reachable effect.
 *
 * <p>⚠ AND IT DOES NOT BYPASS {@code PROFILE_DENIES}. That is the one restriction that must
 * survive, because the toolbar-profile dropdown is how an admin PREVIEWS what ASC and AI readers
 * get — which is precisely the review this catalog exists to support. A catalog that ignored the
 * profile would show everything under every profile and silently destroy the instrument the next
 * decision depends on.
 *
 * <p>It does not bypass the ROLE gate either, and does not need to: the audit measured that gate
 * hiding nothing from an admin, and catalog mode requires an admin to begin with. Leaving it in
 * the chain means a future admin-only rule still applies rather than being quietly exempted.
 *
 * <h2>⚠ SCOPED BY ROLE, which is why {@code PRODUCT_DENIES.astro} was not simply emptied</h2>
 *
 * That list is per-PRODUCT and this is wanted per-ROLE: emptied, every astro CUSTOMER would get
 * the three cycle controls too. Keying on the role means no customer on any product is affected
 * by this file at all, and the list stays intact and still says what it said everywhere else.
 *
 * <h2>One flag reverts it</h2>
 *
 * {@link ASTRO_FULL_CATALOG} to false restores the ordinary astro toolbar exactly, because every
 * bypass goes through {@link showsFullCatalog}.
 */

import { ASTRO } from './productTarget';

/**
 * The master switch.
 *
 * <p>A constant rather than an environment variable: an env var is invisible in a review of the
 * source, and this changes what a toolbar contains. The same reasoning `productTarget.js` gives
 * for preferring its committed JSON over `REACT_APP_PRODUCT`.
 */
export const ASTRO_FULL_CATALOG = true;

/**
 * Whether the full catalog applies.
 *
 * <p>⚠ BOTH CONDITIONS, and `isAdmin` is tested STRICTLY. A truthy check would let a partially
 * resolved auth object — `isAdmin: 'false'`, or an object — open the catalog to whoever held it;
 * this repository already records a profile falling back to ADMIN for every visitor because the
 * role was not yet resolved, and the catalog must not inherit that. Before auth settles `isAdmin`
 * is false, so the toolbar starts restricted and widens, which is the safe direction.
 *
 * @param {string} product  the EFFECTIVE product target
 * @param {boolean} isAdmin the resolved role, from the auth context
 */
export const showsFullCatalog = (product, isAdmin) =>
  ASTRO_FULL_CATALOG === true && isAdmin === true && product === ASTRO;

/**
 * The toolbar's layout in catalog mode.
 *
 * <p>The ordinary toolbar is one non-wrapping row with `overflowX: 'auto'`, which does not clip —
 * it scrolls. That is right for a working bar and wrong for an inventory: roughly eighty icons on
 * one scrolling line means the reader cannot see the set they are being asked to choose from, and
 * the icons past the fold are the newest ones. Wrapping shows all of them at once.
 *
 * <p>Applied ONLY in catalog mode, so the normal toolbar keeps the single row it has always had.
 */
export const CATALOG_LAYOUT = Object.freeze({ flexWrap: 'wrap', overflowX: 'visible' });
